import type { GeneratorOptions, Schema, SchemaField } from '@cli-types/core-engine/schema.js'
import type { FormGeneratorResult } from '@cli-types/next-adapter/forms.js'
import { pluralize, singularize, toKebabCase, toPascalCase } from '@cli-utils/text.js'
import { flattenFields } from '@core-engine/schema/field-helpers/flattening/flatten-fields.js'
import { hasFieldType } from '@core-engine/schema/field-helpers/walking/has-field-type.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths/resolve-schema-action-import-path.js'
import { isAuthorshipFieldName } from '@next-adapter/generators/actions/action-helpers/is-authorship-field-name.js'
import { buildDefaultValueFromSyncFunctions } from '@next-adapter/generators/form/form-shared/build-default-value-from-sync-functions.js'
import { buildDefaultValues } from '@next-adapter/generators/form/form-shared/build-default-values.js'
import { buildUiImports } from '@next-adapter/generators/form/form-shared/build-ui-imports.js'
import { buildZodFields } from '@next-adapter/generators/form/form-shared/build-zod-fields.js'
import { collectDefaultValueFromSourceNames } from '@next-adapter/generators/form/form-shared/collect-default-value-from-source-names.js'
import { collectRelationshipFields } from '@next-adapter/generators/form/form-shared/collect-relationship-fields.js'
import { defaultValueFromHandlerName } from '@next-adapter/generators/form/form-shared/default-value-from-handler-name.js'
import { hasNestedObjectListField } from '@next-adapter/generators/form/form-shared/has-nested-object-list-field.js'
import { hasSimpleListField } from '@next-adapter/generators/form/form-shared/has-simple-list-field.js'
import { renderEntityFormContentRenderer } from '@next-adapter/templates/renderers/generators/form/form-entity/generate-form-content.js'
import { renderEntityFormHookRenderer } from '@next-adapter/templates/renderers/generators/form/form-entity/generate-form-hook.js'
import { buildEntityFormArtifacts } from './build-entity-form-artifacts.js'
import { buildEntityFormLayout } from './build-entity-form-layout.js'
import { collectSidebarSectionHeadingFields } from './collect-sidebar-section-heading-fields.js'

export function generateForm(
  schema: Schema,
  pagesDir: string,
  options: GeneratorOptions = {}
): FormGeneratorResult {
  const actionImportPath = resolveSchemaActionImportPath(schema.name, options.outputNamespace)
  const singular = singularize(schema.name)
  const plural = pluralize(schema.name)
  const Singular = toPascalCase(singular)
  const kebabSingular = toKebabCase(singular)
  const hookName = `use${Singular}Form`
  const hookOptionsName = `Use${Singular}FormOptions`
  const hookPath = `${options.adminDir ?? 'admin'}/hooks/${schema.name}/use-${kebabSingular}-form.ts`
  const hookImportPath = `@admin/hooks/${schema.name}/use-${kebabSingular}-form`
  const contentComponentName = `${Singular}FormContent`
  const adminRoutePath = options.adminRoutePath ?? '/admin'

  const allFormFields = schema.fields.filter(
    (f) =>
      !f.primaryKey &&
      f.name !== 'createdAt' &&
      f.name !== 'updatedAt' &&
      !isAuthorshipFieldName(f.name)
  )
  const flatFields = flattenFields(allFormFields)
  const hasDraft = schema.actions?.draft === true
  const showMetadataWidget = schema.type !== 'single' && schema.metadata !== false

  const tabFieldNames = new Set<string>()
  for (const f of schema.fields) {
    if (f.type === 'tabs' && f.tabs) {
      for (const tab of f.tabs) {
        if (tab.fields) for (const tf of tab.fields) tabFieldNames.add(tf.name)
      }
    }
  }

  const hasBoolean = hasFieldType(schema.fields, 'boolean')
  const hasImage = hasFieldType(schema.fields, 'image')
  const hasVideo = hasFieldType(schema.fields, 'video')
  const hasMedia = hasFieldType(schema.fields, 'media')
  const hasGallery = hasFieldType(schema.fields, 'gallery')
  const hasIcon = hasFieldType(schema.fields, 'icon')
  const hasDate = hasFieldType(schema.fields, 'date')
  const creatableSelectFields = flatFields.filter(
    (f) => f.type === 'select' && f.creatable === true
  )
  const creatableSelectFieldNames = new Set(creatableSelectFields.map((field) => field.name))
  const hasCreatableSelect = creatableSelectFields.length > 0
  const multiSelectFields = flatFields.filter((f) => f.type === 'select' && f.multiple === true)
  const staticMultiSelectFields = multiSelectFields.filter((f) => f.creatable !== true)
  const hasSelectCombobox = hasCreatableSelect || multiSelectFields.length > 0
  const hasSelect = flatFields.some(
    (f) => f.type === 'select' && f.creatable !== true && f.multiple !== true
  )
  const hasMarkdown = hasFieldType(schema.fields, 'markdown')
  const hasRichtext = hasFieldType(schema.fields, 'richtext')
  const hasTextarea = hasFieldType(schema.fields, 'text')
  const hasSeparator = hasFieldType(schema.fields, 'separator')
  const hasRelationship = hasFieldType(schema.fields, 'relationship')
  const hasTabsField = schema.fields.some((f) => f.type === 'tabs')
  const tabsField = schema.fields.find((f) => f.type === 'tabs')
  const tabNames = tabsField?.tabs?.map((tab) => tab.name) ?? []

  const mainRelFields = collectRelationshipFields(schema.fields, tabFieldNames)

  const listFieldsWithNested: SchemaField[] = []
  function collectListFields(fields: SchemaField[]): void {
    for (const f of fields) {
      if (f.type === 'list' && f.fields && f.fields.length > 0 && !f.hidden) {
        listFieldsWithNested.push(f)
      }
      if ((f.type === 'group' || f.type === 'section') && f.fields) collectListFields(f.fields)
      if (f.type === 'tabs' && f.tabs) {
        for (const tab of f.tabs) {
          if (tab.fields) collectListFields(tab.fields)
        }
      }
    }
  }
  collectListFields(allFormFields)
  const hasList = hasFieldType(schema.fields, 'list')
  const hasNestedList = listFieldsWithNested.length > 0
  const hasSimpleList = hasList && hasSimpleListField(allFormFields)
  const hasNestedObjectList = hasNestedObjectListField(allFormFields)
  const sidebarSectionHeadingFields = collectSidebarSectionHeadingFields(allFormFields)
  const hasSectionCardHeading = sidebarSectionHeadingFields.length > 0
  const sectionHeadingIconNames = Array.from(
    new Set(
      sidebarSectionHeadingFields.flatMap((field) => {
        const icon = field.heading?.icon?.trim()
        return icon ? [toPascalCase(icon)] : []
      })
    )
  )

  const zodFields = buildZodFields(flatFields)
  const visibleSlugField = flatFields.find((field) => field.name === 'slug' && !field.hidden)
  const hasSlugTitleSource = Boolean(
    visibleSlugField &&
    flatFields.some(
      (field) => field.name === 'title' && ['string', 'varchar', 'text'].includes(field.type)
    )
  )
  const slugFallbackValue = `${toKebabCase(singular)}-1`
  const slugValueHelperName = 'getGeneratedSlugValue'
  const slugValueHelper = visibleSlugField
    ? renderEntityFormContentRenderer({
        kind: 'renderFormContentTemplate',
        props: {
          slugValueHelperName: slugValueHelperName,
          slugFallbackValue: slugFallbackValue
        }
      })
    : ''

  const defaultValues = buildDefaultValues(flatFields, 'initialData', flatFields, {
    slugFallbackValue: visibleSlugField ? slugFallbackValue : undefined
  })
  const defaultValueFromSourceNames = collectDefaultValueFromSourceNames(flatFields)
  const defaultValueFromSyncFunctions = buildDefaultValueFromSyncFunctions(flatFields, {
    slugValueHelperName: hasSlugTitleSource ? slugValueHelperName : undefined
  })

  const {
    topLevelTabsField,
    topLevelTabComponents,
    contentUsesTabsField,
    contentTabIconNames,
    topLevelTabIconNames,
    formBodyJSX,
    hasSidebarFields,
    shouldGuardSidebarByTab,
    sidebarVisibilityState
  } = buildEntityFormLayout({
    allFormFields,
    schemaName: schema.name,
    Singular,
    hasDraft,
    showMetadataWidget,
    creatableSelectFieldNames,
    defaultValueFromSourceNames,
    tabFieldNames
  })

  const formControlFreeFieldTypes = new Set(['separator', 'tabs'])
  const inputFreeFieldTypes = new Set([
    'boolean',
    'image',
    'video',
    'media',
    'gallery',
    'icon',
    'date',
    'select',
    'richtext',
    'markdown',
    'text',
    'relationship',
    'list',
    'separator',
    'tabs'
  ])
  const contentUsesFormControls = flatFields.some((field) => {
    if (field.primaryKey || field.hidden) return false
    if (field.hasIcon && !['group', 'section', 'tabs', 'separator'].includes(field.type)) {
      return true
    }
    if (field.type === 'list' && (!field.fields || field.fields.length === 0)) return false
    return !formControlFreeFieldTypes.has(field.type)
  })
  const nestedListUsesInput = listFieldsWithNested.some((field) =>
    field.fields?.some(
      (nestedField) => !['boolean', 'image', 'video', 'media'].includes(nestedField.type)
    )
  )
  const contentUsesInput =
    nestedListUsesInput ||
    flatFields.some(
      (field) => !field.primaryKey && !field.hidden && !inputFreeFieldTypes.has(field.type)
    )
  const contentUsesPendingState = flatFields.some(
    (field) => !field.primaryKey && !field.hidden && !['separator', 'tabs'].includes(field.type)
  )
  const fieldRendersMainJSXForImports = (field: SchemaField): boolean => {
    if (field.hidden) return false
    if ((field.type === 'group' || field.type === 'section') && field.fields) {
      return field.fields.some(fieldRendersMainJSXForImports)
    }
    if (field.type === 'tabs') return Boolean(field.tabs?.length)
    return true
  }
  const fieldSupportsIconPostfix = (field: SchemaField): boolean =>
    !['group', 'section', 'tabs', 'separator'].includes(field.type)
  const hasRenderedIconPostfix = (fields: SchemaField[]): boolean => {
    for (const field of fields) {
      if (field.hidden) continue
      if (
        field.hasIcon &&
        fieldSupportsIconPostfix(field) &&
        fieldRendersMainJSXForImports(field)
      ) {
        return true
      }
      if ((field.type === 'group' || field.type === 'section') && field.fields) {
        if (hasRenderedIconPostfix(field.fields)) return true
      }
      if (field.type === 'tabs' && field.tabs) {
        for (const tab of field.tabs) {
          if (hasRenderedIconPostfix(tab.fields ?? [])) return true
        }
      }
    }
    return false
  }
  const contentHasRenderedIconPostfix = hasRenderedIconPostfix(allFormFields)
  const uiImports = buildUiImports({
    hasBoolean,
    hasTextarea,
    hasImage,
    hasVideo,
    hasMedia,
    hasGallery,
    hasIcon,
    hasIconPostfix: contentHasRenderedIconPostfix,
    hasDate,
    hasMarkdown,
    hasRichtext,
    hasSeparator,
    hasSelect,
    hasSelectCombobox,
    hasCreatableSelect,
    hasMultiSelect: multiSelectFields.length > 0,
    hasTabsField: contentUsesTabsField,
    hasRelationship,
    hasSimpleList,
    hasNestedList,
    hasNestedObjectList,
    hasCardContent: true,
    hasSectionCardHeading,
    hasFormRoot: true,
    hasButton: hasRelationship || hasSelectCombobox || hasNestedList,
    hasFormControls: contentUsesFormControls || contentHasRenderedIconPostfix,
    hasInput: contentUsesInput
  })

  const lucideIcons: string[] = []
  const addLucideIcons = (...icons: string[]) => {
    for (const icon of icons) {
      if (!lucideIcons.includes(icon)) lucideIcons.push(icon)
    }
  }
  addLucideIcons(...sectionHeadingIconNames, ...contentTabIconNames)
  if (hasRelationship || hasSelectCombobox) addLucideIcons('Check', 'ChevronsUpDown')
  if (hasCreatableSelect) addLucideIcons('Plus')
  if (hasNestedList) {
    addLucideIcons('Plus', 'X')
  }

  const relHookImports = mainRelFields
    .map((f) => {
      const relPlural = toPascalCase(pluralize(f.relationship || ''))
      return `import { use${relPlural} } from '@admin/hooks/use-${f.relationship}'`
    })
    .filter((v, i, a) => a.indexOf(v) === i)
    .join('\n')

  const relationshipOpenState = mainRelFields
    .map((f) => {
      return `  const [${f.name}Open, set${toPascalCase(f.name)}Open] = React.useState(false)`
    })
    .join('\n')
  const relationshipDataState = Array.from(
    new Set(mainRelFields.map((field) => field.relationship).filter(Boolean))
  )
    .map((relationship) => {
      const relPlural = toPascalCase(pluralize(relationship || ''))
      return `  const { data: ${relationship}Data } = use${relPlural}()`
    })
    .join('\n')
  const relState = [relationshipOpenState, relationshipDataState].filter(Boolean).join('\n')

  const creatableSelectState = creatableSelectFields
    .map((field) => {
      const fieldPascal = toPascalCase(field.name)
      const staticOptions = (field.options ?? [])
        .map(
          (option) =>
            `{ label: ${JSON.stringify(option.label)}, value: ${JSON.stringify(option.value)} }`
        )
        .join(', ')
      return renderEntityFormHookRenderer({
        kind: 'renderFormStaticOptionsTemplate',
        props: {
          fieldName: field.name,
          fieldPascal: fieldPascal,
          singular: Singular,
          schemaName: schema.name,
          staticOptions: staticOptions
        }
      })
    })
    .join('\n')

  const staticMultiSelectState = staticMultiSelectFields
    .map(
      (field) =>
        `  const [${field.name}Open, set${toPascalCase(field.name)}Open] = React.useState(false)`
    )
    .join('\n')

  const fieldArrayHooks = listFieldsWithNested
    .map((field) => {
      const pascalFieldName = toPascalCase(field.name)
      return renderEntityFormHookRenderer({
        kind: 'renderFormFieldArrayTemplate',
        props: {
          fieldName: field.name,
          pascalFieldName: pascalFieldName
        }
      })
    })
    .join('\n')

  const creatableSelectHandlers = creatableSelectFields
    .map((field) => {
      const fieldPascal = toPascalCase(field.name)
      const defaultValueFromHandler = defaultValueFromSourceNames.has(field.name)
        ? `applyDefaultValueFrom${fieldPascal}`
        : null
      const setCreatedOptionValue = field.multiple
        ? renderEntityFormHookRenderer({
            kind: 'renderFormCurrentValueTemplate',
            props: {
              fieldName: field.name,
              defaultValueFromHandlerContent: defaultValueFromHandler
                ? `${defaultValueFromHandler}(nextValues)\n      `
                : ''
            }
          })
        : renderEntityFormHookRenderer({
            kind: 'renderFormContentFieldNameTemplate',
            props: {
              defaultValueFromHandlerContent: defaultValueFromHandler
                ? `${defaultValueFromHandler}(result.option.value)\n      `
                : '',
              fieldName: field.name
            }
          })
      return renderEntityFormHookRenderer({
        kind: 'renderFormTrimmedLabelTemplate',
        props: {
          fieldPascal: fieldPascal,
          singular: Singular,
          fieldName: field.name,
          schemaName: schema.name,
          setCreatedOptionValue: setCreatedOptionValue
        }
      })
    })
    .join('\n\n')

  const uniqueNames = (names: string[]): string[] => Array.from(new Set(names))
  const defaultValueFromHandlerNames = Array.from(defaultValueFromSourceNames).map((sourceName) =>
    defaultValueFromHandlerName(sourceName)
  )
  const relationshipHookReturnNames = mainRelFields.flatMap((field) => [
    `${field.name}Open`,
    `set${toPascalCase(field.name)}Open`,
    `${field.relationship}Data`
  ])
  const creatableSelectReturnNames = creatableSelectFields.flatMap((field) => {
    const fieldPascal = toPascalCase(field.name)
    return [
      `${field.name}Open`,
      `set${fieldPascal}Open`,
      `${field.name}Search`,
      `set${fieldPascal}Search`,
      `create${fieldPascal}OptionPending`,
      `${field.name}Options`,
      `handleCreate${fieldPascal}Option`
    ]
  })
  const staticMultiSelectReturnNames = staticMultiSelectFields.flatMap((field) => [
    `${field.name}Open`,
    `set${toPascalCase(field.name)}Open`
  ])
  const fieldArrayReturnNames = listFieldsWithNested.flatMap((field) => [
    `${field.name}Expanded`,
    `set${toPascalCase(field.name)}Expanded`,
    `${field.name}FieldArray`
  ])
  const hookReturnNames = uniqueNames([
    'initialData',
    'form',
    'isPending',
    'isDirty',
    ...(hasDraft
      ? [
          'submitIntent',
          'canPublishPristineDraft',
          'canSubmitDraftAction',
          'canSubmitPublishAction'
        ]
      : []),
    ...(hasTabsField ? ['activeTab', 'setActiveTab'] : []),
    ...relationshipHookReturnNames,
    ...creatableSelectReturnNames,
    ...staticMultiSelectReturnNames,
    ...defaultValueFromHandlerNames,
    ...fieldArrayReturnNames,
    'onSubmit',
    'handleErrors'
  ])
  const hookReturnObject = hookReturnNames.map((name) => `    ${name}`).join(',\n')
  const contentUsesInitialData =
    hasDraft || (!topLevelTabsField && hasSidebarFields && showMetadataWidget)
  const standardContentStateNames = uniqueNames([
    ...(contentUsesInitialData ? ['initialData'] : []),
    'form',
    ...(contentUsesPendingState ? ['isPending'] : []),
    ...(contentUsesTabsField ? ['activeTab', 'setActiveTab'] : []),
    ...relationshipHookReturnNames,
    ...creatableSelectReturnNames,
    ...staticMultiSelectReturnNames,
    ...defaultValueFromHandlerNames,
    ...fieldArrayReturnNames,
    'onSubmit',
    'handleErrors'
  ])
  const topLevelTabRouterStateNames = uniqueNames([
    ...(hasDraft ? ['initialData'] : []),
    'form',
    'onSubmit',
    'handleErrors'
  ])
  const contentStateNames = topLevelTabsField
    ? topLevelTabRouterStateNames
    : standardContentStateNames
  const contentStateDestructure = contentStateNames.map((name) => `    ${name}`).join(',\n')
  const tabParserConst = hasTabsField
    ? renderEntityFormHookRenderer({
        kind: 'renderFormTabValuesTemplate',
        props: {
          map: tabNames.map((name) => JSON.stringify(name)).join(', ')
        }
      })
    : ''
  const hookContent = renderEntityFormHookRenderer({
    kind: 'renderEntityFormHook',
    props: {
      hasCreatableSelect,
      hasNestedList,
      hasTabsField,
      hasVisibleSlugField: Boolean(visibleSlugField),
      relHookImportsContent: relHookImports ? `\n${relHookImports}\n` : '',
      singular: Singular,
      actionImportPath,
      tabParserConst,
      zodFields,
      hookOptionsName,
      slugValueHelper,
      hookName,
      relStateContent: relState ? `\n${relState}` : '',
      creatableSelectStateContent: creatableSelectState ? `\n${creatableSelectState}` : '',
      staticMultiSelectStateContent: staticMultiSelectState ? `\n${staticMultiSelectState}` : '',
      hasDraft,
      defaultValues,
      plural,
      adminRoutePath,
      schemaName: schema.name,
      singularText: singular,
      defaultValueFromSyncFunctionsContent: defaultValueFromSyncFunctions
        ? `\n${defaultValueFromSyncFunctions}\n`
        : '',
      fieldArrayHooksContent: fieldArrayHooks ? `\n${fieldArrayHooks}\n` : '',
      creatableSelectHandlersContent: creatableSelectHandlers
        ? `${creatableSelectHandlers}\n\n`
        : '',
      hookReturnObject
    }
  })

  return buildEntityFormArtifacts({
    schema,
    pagesDir,
    hookPath,
    hookImportPath,
    actionImportPath,
    plural,
    Singular,
    hookName,
    contentComponentName,
    hookContent,
    uiImports,
    lucideIcons,
    hasRelationship,
    hasSelectCombobox,
    shouldGuardSidebarByTab,
    showMetadataWidget,
    hasTabsField,
    hasDraft,
    topLevelTabsField,
    topLevelTabComponents,
    topLevelTabIconNames,
    hookReturnNames,
    contentStateDestructure,
    sidebarVisibilityState,
    formBodyJSX
  })
}
